import { ChangeDetectionStrategy, Component, effect, inject, input, output, viewChild } from '@angular/core';

import 'chartjs-adapter-moment';
import { CrosshairPlugin } from '../plugins/chartjs-plugin-crosshair.esm';
import { interpolate } from '../plugins/interpolate';
import { TranslateService } from '@ngx-translate/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, Interaction, InteractionModeFunction } from 'chart.js';
import type { DatapointClickEvent } from '../multi-line-chart/multi-line-chart.component';
import { ChartOptionsButtonComponent } from '../chart-options/chart-options-button.component';
import { ChartOptionsConfig } from '../chart-options/chart-options-storage.service';
import { applyYAxisMode } from '../chart-options/apply-y-axis-mode';
import { ChartLegendComponent } from '../chart-legend/chart-legend.component';
import { HtmlLegendItem, HtmlLegendPlugin } from '../chart-legend/chartjs-plugin-html-legend';

Chart.register(...registerables);

declare module 'chart.js' {
  interface InteractionModeMap {
    interpolate: InteractionModeFunction;
  }
}

Interaction.modes.interpolate = interpolate;

@Component({
  selector: 'cosmos-dynamic-line-chart',
  imports: [BaseChartDirective, ChartOptionsButtonComponent, ChartLegendComponent],
  templateUrl: './dynamic-line-chart.component.html',
  styles: [`
    :host {
      display: block;
      min-width: 0;
    }
    .chart-wrapper {
      position: relative;
      width: 100%;
      min-width: 0;
      height: 450px;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
    .chart-ribbon {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicLineChartComponent {
  private readonly translateService = inject(TranslateService);
  private readonly chart = viewChild(BaseChartDirective);

  readonly dataObject = input<any>(null);
  readonly datasets = input<string[]>([]);
  readonly labels = input<string[]>([]);
  readonly params = input<any>({});
  readonly thresholds = input<any>({});

  /** Stable id for the per-chart options button/localStorage persistence.
   *  Omit to not render the options button at all. */
  readonly chartId = input<string | undefined>(undefined);

  /** Emits when the user clicks a data point (nearest point to the cursor). */
  readonly datapointClick = output<DatapointClickEvent>();

  readonly chartType = 'line';
  // HtmlLegendPlugin no-ops unless chartOptions.plugins.htmlLegend is set.
  readonly chartPlugins = [CrosshairPlugin, HtmlLegendPlugin];
  chartData: { datasets: any[]; labels: any[] } = { datasets: [], labels: [] };
  chartOptions: any;

  /** Current legend items — HtmlLegendPlugin (afterUpdate) refreshes this
   *  whenever chart data/visibility changes; ChartLegendComponent renders it. */
  legendItems: HtmlLegendItem[] = [];

  /** Current Y-axis display preference from ChartOptionsButtonComponent.
   *  No threshold-bands concept here (the inline `thresholds` datasets are a
   *  different mechanism), so 'auto-bands' is never offered. */
  private _yAxisConfig: ChartOptionsConfig = { yAxisMode: 'auto-data' };

  /** chart datasetIndex → the `datasets()` field name that dataset renders
   *  (null for interleaved threshold datasets). Rebuilt in buildChartData(). */
  private _datasetField: (string | null)[] = [];

  constructor() {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: 'time',
          ticks: { major: { enabled: true } },
        },
        y: { type: 'linear', ticks: {} },
      },
      plugins: {
        tooltip: {
          enabled: true,
          callbacks: {
            footer: (tooltipItems: any[]) => {
              const dataObj = this.dataObject();
              if (!dataObj?.time?.[0]) return '';
              const relativeTime = ((new Date(tooltipItems[0].parsed.x).getTime() - new Date(dataObj.time[0]).getTime()) / 1000 / 60 / 60).toFixed(2);
              if (Number(relativeTime) <= 48) {
                return relativeTime + ' horas desde el inicio';
              }
              return (Number(relativeTime) / 24).toFixed(2) + ' días desde el inicio';
            },
          },
          mode: 'index',
          intersect: false,
        },
        crosshair: { zoom: { enabled: true } },
        legend: { display: false },
        htmlLegend: { onLabelsChange: (items: HtmlLegendItem[]) => { this.legendItems = items; } },
      },
      onClick: (event: any, _elements: any[], chart: any) => {
        if (!chart) return;
        const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true);
        if (!points?.length) return;
        const { datasetIndex, index } = points[0];
        const field = this._datasetField[datasetIndex];
        if (!field) return; // threshold dataset — ignore
        const parsed = chart.data?.datasets?.[datasetIndex]?.data?.[index] ?? {};
        this.datapointClick.emit({
          sourceObject: this.dataObject(),
          index,
          variable: field,
          x: parsed.x,
          y: parsed.y,
        });
      },
    };

    effect(() => {
      // Re-run whenever dataObject or datasets change
      const dataObj = this.dataObject();
      const dsNames = this.datasets();
      if (dataObj && dsNames.length) {
        this.buildChartData();
        this.chart()?.update();
      }
    });
  }

  buildChartData(): void {
    const dataObj = this.dataObject();
    const time: Date[] = (dataObj?.time ?? []).map((e: any) => new Date(e));
    const datasets: any[] = [];
    const datasetField: (string | null)[] = [];

    this.datasets().forEach((element, index) => {
      datasets.push(
        this.generateDataset(
          time,
          dataObj[element],
          this.translateService.instant('chart.legend.' + this.labels()[index])
        )
      );
      datasetField.push(element);

      const thresholds = this.thresholds();
      if (thresholds?.[element]) {
        datasets.push(
          this.generateDataset(
            [time[0], time[time.length - 1]],
            [thresholds[element], thresholds[element]],
            `${this.translateService.instant('chart.legend.threshold')} - ${this.translateService.instant('chart.legend.' + this.labels()[index])}`,
            '',
            'threshold'
          )
        );
        datasetField.push(null);
      }
    });

    this._datasetField = datasetField;

    // Replace reference so ng2-charts detects the change under OnPush
    this.chartData = { ...this.chartData, datasets };
  }

  generateDataset(dataX: any[], dataY: any[], label: string, color?: any, type?: string) {
    const data = dataX.map((x: any, i: number) => ({ x, y: dataY[i] }));
    const dataset: any = {
      showLine: true,
      fill: false,
      pointRadius: 2,
      label,
      data,
      lineTension: 0,
      interpolate: true,
      cubicInterpolationMode: 'monotone',
    };
    if (type === 'threshold') dataset['borderDash'] = [5, 5];
    return dataset;
  }

  /** ChartOptionsButtonComponent (configChange) handler.
   *  On the very first call (right after this component's own construction,
   *  which always builds chartOptions with the {yAxisMode:'auto-data'}
   *  default since ChartOptionsButtonComponent hasn't read localStorage yet
   *  at that point), viewChild(BaseChartDirective) isn't resolved yet either
   *  — it only resolves after ngAfterViewInit, which runs AFTER every
   *  child's ngOnInit, including this button's. Mutating chartOptions.scales.y
   *  in place + calling this.chart()?.update() is then a no-op: chart() is
   *  still undefined, so nothing redraws, and the object reference never
   *  changed so Angular's [options]="chartOptions" binding doesn't re-sync it
   *  either — the chart would silently keep the stale auto-data default from
   *  construction (same root cause fixed in MultiLineChartComponent). So:
   *  reassign chartOptions to a NEW object when there's no live chart yet, so
   *  Angular's own change detection picks up the reference change on the
   *  canvas's [options] binding once the chart does get created. */
  onChartOptionsChange(config: ChartOptionsConfig): void {
    this._yAxisConfig = config;

    if (!this.chart()) {
      // No live chart yet — reassign to a fresh object (not just mutate in
      // place) so Angular's [options]="chartOptions" binding actually
      // re-syncs into chart.js once the chart is created.
      this.chartOptions = { ...this.chartOptions, scales: { ...this.chartOptions.scales, y: { ...this.chartOptions.scales.y } } };
      applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, null);
      return;
    }

    applyYAxisMode(this.chartOptions.scales.y, this._yAxisConfig, null);
    this.chart()?.update();
  }

  /** ChartLegendComponent (toggle) handler — click a legend item to show/hide
   *  that dataset (see MultiLineChartComponent.onLegendToggle). */
  onLegendToggle(datasetIndex: number): void {
    const chart = this.chart();
    if (!chart) return;
    chart.hideDataset(datasetIndex, !chart.isDatasetHidden(datasetIndex));
  }
}
